import { handleActions } from 'redux-actions';
import {
  receiveCommentsForFunction,
  receiveRecentComments,
  receiveCommentCount,
  commentAdded,
  commentUpdated,
  commentRemoved,
  markRecentRead,
  markFunctionRead,
} from './commentsActions';

const LAST_READ_KEY = 'cra:commentsLastRead';
const LAST_READ_BY_FN_KEY = 'cra:commentsLastReadByFunction';

const readLastRead = () => {
  try {
    const v = localStorage.getItem(LAST_READ_KEY);
    return v ? new Date(v).toISOString() : null;
  } catch {
    return null;
  }
};

const writeLastRead = (iso) => {
  try {
    localStorage.setItem(LAST_READ_KEY, iso);
  } catch {
    /* localStorage unavailable */
  }
};

const readLastReadByFn = () => {
  try {
    const v = localStorage.getItem(LAST_READ_BY_FN_KEY);
    return v ? JSON.parse(v) : {};
  } catch {
    return {};
  }
};

const writeLastReadByFn = (obj) => {
  try {
    localStorage.setItem(LAST_READ_BY_FN_KEY, JSON.stringify(obj));
  } catch {
    /* localStorage unavailable */
  }
};

const defaultState = {
  byFunction: {},
  counts: {},
  recent: [],
  lastReadAt: readLastRead(),
  lastReadByFunction: readLastReadByFn(),
};

const upsertById = (list, comment) => {
  const idx = list.findIndex((c) => c.commentId === comment.commentId);
  if (idx >= 0) {
    const next = list.slice();
    next[idx] = comment;
    return next;
  }
  return [comment, ...list];
};

export default handleActions(
  {
    [receiveCommentsForFunction]: (state, action) => {
      const { functionId, comments } = action.payload;
      // Only cache the (paged) list here. The authoritative per-function count comes
      // from receiveCommentCount; overwriting it with a page length (hover fetches 5,
      // the thread 50) would clobber the real total shown on the badge/footer.
      return {
        ...state,
        byFunction: { ...state.byFunction, [functionId]: comments },
      };
    },
    [receiveRecentComments]: (state, action) => ({
      ...state,
      recent: action.payload.comments,
    }),
    [receiveCommentCount]: (state, action) => {
      const { functionId, total } = action.payload;
      return { ...state, counts: { ...state.counts, [functionId]: total } };
    },
    [commentAdded]: (state, action) => {
      const comment = action.payload;
      const existing = state.byFunction[comment.functionId] || [];
      return {
        ...state,
        byFunction: {
          ...state.byFunction,
          [comment.functionId]: upsertById(existing, comment),
        },
        counts: {
          ...state.counts,
          [comment.functionId]: (state.counts[comment.functionId] || 0) + 1,
        },
        recent: upsertById(state.recent, comment),
      };
    },
    [commentUpdated]: (state, action) => {
      const comment = action.payload;
      const existing = state.byFunction[comment.functionId] || [];
      return {
        ...state,
        byFunction: {
          ...state.byFunction,
          [comment.functionId]: upsertById(existing, comment),
        },
        recent: state.recent.map((c) => (c.commentId === comment.commentId ? comment : c)),
      };
    },
    [commentRemoved]: (state, action) => {
      const { functionId, commentId } = action.payload;
      const existing = state.byFunction[functionId] || [];
      return {
        ...state,
        byFunction: {
          ...state.byFunction,
          [functionId]: existing.filter((c) => c.commentId !== commentId),
        },
        counts: {
          ...state.counts,
          [functionId]: Math.max(0, (state.counts[functionId] || 1) - 1),
        },
        recent: state.recent.filter((c) => c.commentId !== commentId),
      };
    },
    [markRecentRead]: (state, action) => {
      const { timestamp } = action.payload || {};
      if (!timestamp) return state;
      if (state.lastReadAt && state.lastReadAt >= timestamp) return state;
      writeLastRead(timestamp);
      return { ...state, lastReadAt: timestamp };
    },
    [markFunctionRead]: (state, action) => {
      const { functionId, timestamp } = action.payload;
      if (!functionId || !timestamp) return state;
      const existing = state.lastReadByFunction[functionId];
      if (existing && existing >= timestamp) return state;
      const next = { ...state.lastReadByFunction, [functionId]: timestamp };
      writeLastReadByFn(next);
      return { ...state, lastReadByFunction: next };
    },
  },
  defaultState
);
